import React, { useEffect, useState } from "react";
import api, { fmt, fmtNum } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, StatCard, PageHeader, RagDot, ragText, Empty } from "@/components/common";
import { TRACKER_SECTIONS } from "@/lib/trackerConfig";
import { ShieldAlert, HeartPulse, TrendingUp, Users, CalendarCheck, AlertTriangle, CheckCircle2, Info, Star, Video, Smile } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Cell
} from "recharts";

const tooltipStyle = { background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 12 };

export default function Dashboard() {
  const { filters, technicians } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { start: filters.start, end: filters.end };
    if (filters.branch !== "all") params.branch = filters.branch;
    if (filters.technicianId !== "all") params.technician_id = filters.technicianId;
    api.get("/dashboard", { params }).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters]);

  if (loading || !data) return <Empty text="Loading dashboard…" />;
  const s = data.summary;
  const techName = technicians.find((t) => t.employee_id === filters.technicianId)?.name || filters.technicianId;
  const insightIcon = { positive: <CheckCircle2 className="text-emerald-400" size={18} />, warning: <AlertTriangle className="text-[#C9A24B]" size={18} />, critical: <ShieldAlert className="text-[#DC2626]" size={18} /> };

  return (
    <div className="animate-fade-up" data-testid="dashboard-page">
      <PageHeader title="Executive Dashboard" subtitle={data.mission} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard testid="stat-dosing" label="Dosing Errors" value={s.dosing_errors} tone={s.dosing_errors === 0 ? "success" : "danger"} sub={s.dosing_errors === 0 ? "Target achieved" : "ACTION REQUIRED"} icon={<ShieldAlert size={18} />} />
        <StatCard testid="stat-attendance" label="Attendance Rate" value={`${s.attendance_rate}%`} tone="default" icon={<CalendarCheck size={18} />} />
        <StatCard testid="stat-procedures" label="Procedures" value={fmtNum(s.procedures)} icon={<HeartPulse size={18} />} />
        <StatCard testid="stat-complications" label="Complications" value={s.complications} tone={s.complications === 0 ? "success" : "danger"} icon={<AlertTriangle size={18} />} />
        <StatCard testid="stat-crosssell" label="Cross-sell Value" value={fmt(s.upsell_value)} tone="gold" sub={`${s.upsell_count} opportunities`} icon={<TrendingUp size={18} />} />
        <StatCard testid="stat-feedback" label="Feedback Completion" value={`${s.feedback_completion ?? 0}%`} tone={(s.feedback_completion ?? 0) >= 90 ? "success" : "gold"} sub="Doctor + Patient" icon={<CheckCircle2 size={18} />} />
        <StatCard testid="stat-patient-satisfaction" label="Patient Satisfaction" value={s.patient_satisfaction === null || s.patient_satisfaction === undefined ? "--" : `${s.patient_satisfaction}%`} tone={(s.patient_satisfaction ?? 0) >= 75 ? "success" : "gold"} sub="Comm. + Hygiene + Privacy + OT Exp." icon={<Smile size={18} />} />
        <StatCard testid="stat-reviews" label="Total Reviews" value={fmtNum(s.reviews)} icon={<Star size={18} />} />
        <StatCard testid="stat-testimonials" label="Total Testimonials" value={fmtNum(s.testimonials)} icon={<Video size={18} />} />
        <StatCard testid="stat-avg-result" label="Avg 7-Day Result" value={data.followups?.avg_result_pct == null ? "--" : `${data.followups.avg_result_pct}%`} tone={(data.followups?.avg_result_pct ?? 0) >= 75 ? "success" : "gold"} icon={<HeartPulse size={18} />} />
        <StatCard testid="stat-pending-fu" label="Pending 7-Day Feedback" value={data.followups?.pending ?? 0} tone={(data.followups?.pending ?? 0) > 0 ? "danger" : "success"} icon={<AlertTriangle size={18} />} />
        <StatCard testid="stat-completed-fu" label="Completed 7-Day Feedback" value={data.followups?.completed ?? 0} icon={<CheckCircle2 size={18} />} />
      </div>

      {data.attendance_by_technician && data.attendance_by_technician.length > 0 && (
        <Card className="p-5 mb-8" data-testid="attendance-by-technician">
          <h3 className="font-heading text-lg mb-1">Attendance by Technician</h3>
          <p className="text-xs text-slate-500 mb-4">Present days ÷ Total working days × 100 ({filters.start} → {filters.end})</p>
          <div className="overflow-x-auto max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wider bg-white/5">
                  <th className="py-2 px-3">Technician</th><th className="px-3">Present</th><th className="px-3">Working Days</th><th className="px-3">Attendance %</th>
                </tr>
              </thead>
              <tbody>
                {data.attendance_by_technician.map((r) => (
                  <tr key={r.technician_id} className="border-t border-white/5 hover:bg-white/5" data-testid={`att-${r.technician_id}`}>
                    <td className="px-3 py-2.5">{r.name}</td>
                    <td className="px-3">{r.present}</td>
                    <td className="px-3">{r.working}</td>
                    <td className={`px-3 font-semibold ${r.attendance_pct >= 90 ? "text-emerald-400" : r.attendance_pct >= 75 ? "text-[#C9A24B]" : "text-[#DC2626]"}`}>{r.attendance_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {filters.technicianId !== "all" && (
        <Card className="p-5 mb-8" data-testid="tracker-details-panel">
          <h3 className="font-heading text-lg mb-1">Daily Tracker — {techName}</h3>
          <p className="text-xs text-slate-500 mb-4">Total Procedures Assisted Today &amp; Remarks, mapped by technician + date ({filters.start} → {filters.end})</p>
          {(!data.tracker_details || data.tracker_details.length === 0) ? (
            <Empty text="Not Entered — no daily tracker submitted for this technician in the selected date range." />
          ) : (
            <div className="space-y-5">
              {data.tracker_details.map((d, i) => (
                <div key={i} className="bg-[#0b1120] border border-white/10 rounded-lg p-4" data-testid={`tracker-detail-${d.date}`}>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mb-3">
                    <span className="font-heading text-[#C9A24B]">{d.date}</span>
                    <span className="text-sm text-slate-300">Total Procedures Assisted Today: <span className="font-semibold text-white" data-testid={`detail-procedures-${d.date}`}>{(d.procedures_assisted === null || d.procedures_assisted === undefined) ? "—" : d.procedures_assisted}</span></span>
                    <span className="text-sm text-slate-300">Remark: <span className="text-slate-200">{d.remarks && d.remarks.trim() ? d.remarks : "—"}</span></span>
                    <span className="text-sm text-slate-300">Reviews: <span className="font-semibold text-emerald-400">{d.reviews ?? 0}</span></span>
                    <span className="text-sm text-slate-300">Testimonials: <span className="font-semibold text-emerald-400">{d.testimonials ?? 0}</span></span>
                    <span className="text-sm text-slate-300">Training: <span className="text-slate-200">{d.training_status || "None"}</span></span>
                    <span className="text-sm text-slate-300">Issue: <span className={`font-semibold ${d.has_issue ? "text-[#DC2626]" : "text-emerald-400"}`}>{d.has_issue ? `Yes${d.issue_note ? " — " + d.issue_note : ""}` : "No"}</span></span>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
                    {TRACKER_SECTIONS.map((sec) => (
                      <div key={sec.title}>
                        <h4 className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">{sec.title}</h4>
                        {sec.fields.map(([k, label]) => {
                          const v = d.counts ? d.counts[k] : undefined;
                          const na = v === null || v === undefined;
                          return (
                            <div key={k} className="flex items-center justify-between text-xs py-1 border-b border-white/5">
                              <span className="text-slate-400 pr-2">{label}</span>
                              <span data-testid={`detail-count-${d.date}-${k}`} className={`font-semibold ${na ? "text-slate-600" : "text-[#C9A24B]"}`}>{na ? "—" : v}</span>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {data.ot_head_feedback && (
        <Card className="p-5 mb-8" data-testid="ot-head-feedback-panel">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
            <div>
              <h3 className="font-heading text-lg">OT Head Feedback Score</h3>
              <p className="text-xs text-slate-500">Based on {data.ot_head_feedback.entries} entries</p>
            </div>
            <span className="text-3xl font-heading font-semibold text-[#C9A24B]" data-testid="ot-head-feedback-overall">
              {data.ot_head_feedback.overall === null ? "--" : `${data.ot_head_feedback.overall}%`}
            </span>
          </div>
          {data.ot_head_feedback.entries === 0 ? <Empty text="No OT Head feedback in this period." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-400 text-xs uppercase tracking-wider bg-white/5">
                    <th className="py-2 px-3">Technician</th><th className="px-3">Entries</th><th className="px-3">Feedback Score</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ot_head_feedback.by_technician.map((r) => (
                    <tr key={r.technician_id} className="border-t border-white/5 hover:bg-white/5" data-testid={`oth-score-${r.technician_id}`}>
                      <td className="px-3 py-2.5">{r.name}</td>
                      <td className="px-3">{r.entries}</td>
                      <td className={`px-3 font-semibold ${r.score >= 75 ? "text-emerald-400" : r.score >= 50 ? "text-[#C9A24B]" : "text-[#DC2626]"}`}>{r.score}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {data.owner_feedback_score && (
        <Card className="p-5 mb-8" data-testid="owner-feedback-panel">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
            <div>
              <h3 className="font-heading text-lg">Owner Feedback Score</h3>
              <p className="text-xs text-slate-500">Based on {data.owner_feedback_score.entries} entries</p>
            </div>
            <span className="text-3xl font-heading font-semibold text-[#C9A24B]" data-testid="owner-feedback-overall">
              {data.owner_feedback_score.overall === null ? "--" : `${data.owner_feedback_score.overall}%`}
            </span>
          </div>
          {data.owner_feedback_score.entries === 0 ? <Empty text="No Owner feedback in this period." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-400 text-xs uppercase tracking-wider bg-white/5">
                    <th className="py-2 px-3">Technician</th><th className="px-3">Entries</th><th className="px-3">Feedback Score</th>
                  </tr>
                </thead>
                <tbody>
                  {data.owner_feedback_score.by_technician.map((r) => (
                    <tr key={r.technician_id} className="border-t border-white/5 hover:bg-white/5" data-testid={`owner-score-${r.technician_id}`}>
                      <td className="px-3 py-2.5">{r.name}</td>
                      <td className="px-3">{r.entries}</td>
                      <td className={`px-3 font-semibold ${r.score >= 75 ? "text-emerald-400" : r.score >= 50 ? "text-[#C9A24B]" : "text-[#DC2626]"}`}>{r.score}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
        {/* Scorecard */}
        <Card className="xl:col-span-2 p-5">
          <h3 className="font-heading text-lg mb-4">7-Pillar KPI Scorecard</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wider border-b border-white/10">
                  <th className="py-2">Pillar / Metric</th><th className="py-2">Target</th><th className="py-2">Actual</th><th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.scorecard.map((r, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5" data-testid={`scorecard-row-${i}`}>
                    <td className="py-2.5"><div className="text-slate-200">{r.metric}</div><div className="text-[10px] text-slate-500">{r.pillar}</div></td>
                    <td className="py-2.5 text-slate-400">{r.target}</td>
                    <td className={`py-2.5 font-semibold ${r.critical && r.actual > 0 ? "text-[#DC2626]" : ragText[r.status]}`}>
                      {r.unit === "₹" ? fmt(r.actual) : `${r.actual}${r.unit === "%" ? "%" : r.unit === "/5" ? "/5" : ""}`}
                    </td>
                    <td className="py-2.5"><RagDot status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Insights */}
        <Card className="p-5">
          <h3 className="font-heading text-lg mb-4 flex items-center gap-2"><Info size={18} className="text-[#C9A24B]" /> Auto Insights</h3>
          <div className="space-y-3">
            {data.insights.map((ins, i) => (
              <div key={i} className="flex gap-3 items-start bg-white/5 rounded-md p-3" data-testid={`insight-${i}`}>
                {insightIcon[ins.type]}
                <p className="text-sm text-slate-300">{ins.text}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-heading text-lg mb-4">Branch Attendance & OT Readiness</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.branch_stats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="branch" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="attendance" name="Attendance %" fill="#059669" radius={[4,4,0,0]} />
              <Bar dataKey="prep" name="OT Prep %" fill="#C9A24B" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-heading text-lg mb-4">Dosing Error Trend <span className="text-xs text-slate-500">(should trend to zero)</span></h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.dosing_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="errors" name="Dosing Errors" stroke="#DC2626" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-heading text-lg mb-4">Surgeries per Branch</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.branch_stats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="branch" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="surgeries" name="Procedures" fill="#38bdf8" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-heading text-lg mb-4">Cross-sell Revenue by Technician</h3>
          {data.cross_sell.length === 0 ? <Empty text="No cross-sell logged in this period." /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.cross_sell} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis type="number" stroke="#64748b" fontSize={11} tickFormatter={(v) => `₹${(v/1000)}k`} />
                <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={11} width={90} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmt(v)} />
                <Bar dataKey="value" name="Upsell Value" radius={[0,4,4,0]}>
                  {data.cross_sell.map((e, i) => <Cell key={i} fill="#C9A24B" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </div>
  );
}
